import React, { lazy, useMemo } from 'react';
import Loadable from 'app/components/Loadable/Loadable';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';

// Admin Dashboard Routes
const Analytics = Loadable(lazy(() => import("../dashboard/Analytics")));
const Users = Loadable(lazy(() => import("../dashboard/users")));
const UserLog = Loadable(lazy(() => import("../dashboard/UserLog")));
const Participants = Loadable(lazy(() => import("../dashboard/Participants")));
const Transactions = Loadable(lazy(() => import("../dashboard/Transactions")));
const Raffles = Loadable(lazy(() => import("../dashboard/Raffles")));
const RaffleDetails = Loadable(lazy(() => import("../dashboard/Raffles/details")));
const Exhibitors = Loadable(lazy(() => import("../dashboard/Exhibitors")));
const ExhibitorsDetails = Loadable(lazy(() => import("../dashboard/Exhibitors/details")));
const Settings = Loadable(lazy(() => import("../dashboard/Settings")));
const ScansAdmin = Loadable(lazy(() => import("../dashboard/Scans")));

// Organizer-side Routes
const ExhibitorsOrganizer = Loadable(lazy(() => import("../organizer/Exhibitors")));
const ExhibitorsOrganizerDetails = Loadable(lazy(() => import("../organizer/Exhibitors/details")));

const ParticipantsOrganizer = Loadable(lazy(() => import("../organizer/Participants")));
const RafflesOrganizer = Loadable(lazy(() => import("../organizer/Raffles")));
const RaffleDetailsOrganizer = Loadable(lazy(() => import("../organizer/Raffles/details")));
const EventsDetailsOrganizer = Loadable(lazy(() => import("../organizer/Events")));



// Exhibitor-side Routes
const Booths = Loadable(lazy(() => import("./Booths")));
const BoothsDetails = Loadable(lazy(() => import("./Booths/details")));
const BoothsEdit = Loadable(lazy(() => import("./Booths/edit")));
const ActivitiesEdit = Loadable(lazy(() => import("./Booths/activities")));
const Scans = Loadable(lazy(() => import("./Scans")));
const Staff = Loadable(lazy(() => import("./Staff")));
const Profile = Loadable(lazy(() => import("./Profile")));
const AnalyticsDetails = Loadable(lazy(() => import("./Analytics")));

const roleRedirects = {
    Admin: '/raffles',
    Organizer: '/exhibitors',
    ExhibitAdmin: '/booths',
    ExhibitAmbassador: '/boothscans',
};

// Route definitions per role
const NAV_ROUTES = {
    Admin: [
        { path: '/raffles', element: <Raffles /> },
        { path: '/raffles/:id', element: <RaffleDetails /> },
        { path: '/raffles/new', element: <RaffleDetails /> },

        { path: '/exhibitors', element: <Exhibitors /> },
        { path: '/exhibitors/:id', element: <ExhibitorsDetails /> },
        { path: '/exhibitors/new', element: <ExhibitorsDetails /> },

        { path: '/participants', element: <Participants /> },
        { path: '/scans', element: <ScansAdmin /> },
        { path: '/transactions', element: <Transactions /> },
        { path: '/users', element: <Users /> },
        { path: '/settings', element: <Settings /> },
        { path: '/userlogs', element: <UserLog /> },
    ],
    Organizer: [
        { path: '/exhibitors', element: <ExhibitorsOrganizer /> },
        { path: '/exhibitors/:id', element: <ExhibitorsOrganizerDetails /> },
        { path: '/exhibitors/new', element: <ExhibitorsOrganizerDetails /> },

        { path: '/participants', element: <ParticipantsOrganizer /> },
        { path: '/raffles', element: <RafflesOrganizer /> },

        { path: '/raffles/:id', element: <RaffleDetailsOrganizer /> },
        { path: '/raffles/new', element: <RaffleDetailsOrganizer /> },

        { path: '/events', element: <EventsDetailsOrganizer /> },

        { path: '/profile', element: <Profile /> },

    ],
    ExhibitAdmin: [
        { path: '/booths', element: <Booths /> },
        { path: '/boothscans', element: <Scans /> },
        { path: '/staff', element: <Staff /> },
        { path: '/profile', element: <Profile /> },

        { path: '/booths/:id', element: <BoothsDetails /> },
        { path: '/booths/edit/:id', element: <BoothsEdit /> },
        { path: '/activities/edit/:id', element: <ActivitiesEdit /> },
        { path: '/analytics/:id', element: <AnalyticsDetails /> },
    ],
    ExhibitAmbassador: [
        { path: '/boothscans', element: <Scans /> },
        { path: '/profile', element: <Profile /> },
    ],
};

// Hook to get role-specific routes
export function useRoleRoutes() {
    const role = useSelector((state) => state.user.user?.role);
    return useMemo(() => NAV_ROUTES[role] || [], [role]);
}

// 🔁 Inline RoleRedirect Component
export const RoleRedirect = () => {
    const role = useSelector((state) => state.user.user?.role);

    if (!role) return <Navigate to="/login" />;

    return <Navigate to={roleRedirects[role] || '/profile'} />;
};

// Static routes that don't show bottom bar
export const exhibitorRoutesNoBottomBar = [
    { path: '/booths/:id', element: <BoothsDetails /> },
    { path: '/booths/edit/:id', element: <BoothsEdit /> },
    { path: '/activities/edit/:id', element: <ActivitiesEdit /> },
    { path: '/analytics/:id', element: <AnalyticsDetails /> },
];
